import { useState, useMemo } from 'react';
import { PaymentOrder } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';

/** All list-filter state for the Payment Orders page (search, selects, dates, amounts) */
export function usePaymentOrderFilters(
  paymentOrders: PaymentOrder[],
  providers: any[],
  banks: any[],
  currencies: any[],
  getProviderName: (id: string) => string,
  getBankName: (id?: string) => string
) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [bankFilter, setBankFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');

  const filteredPaymentOrders = useMemo(() => {
    return paymentOrders.filter(order => {
      const provider = getProviderName(order.providerId);
      const bank = getBankName(order.bankId);
      const matchesSearch = !searchTerm || (
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.referenceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.requestedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bank.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      const matchesProvider = providerFilter === 'all' || order.providerId === providerFilter;
      const matchesBank = bankFilter === 'all' || (order.bankId && order.bankId === bankFilter);
      const matchesCurrency = currencyFilter === 'all' || resolveCurrencyCode(order.currency) === currencyFilter;
      const orderDate = new Date(order.date);
      const matchesDateFrom = !dateFromFilter || orderDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || orderDate <= new Date(dateToFilter);
      const amount = order.total || 0;
      const matchesAmountFrom = !amountFromFilter || amount >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || amount <= parseFloat(amountToFilter);
      return matchesSearch && matchesStatus && matchesProvider && matchesBank &&
        matchesCurrency && matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [paymentOrders, searchTerm, statusFilter, providerFilter, bankFilter, currencyFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, getProviderName, getBankName]);

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setProviderFilter('all'); setBankFilter('all');
    setCurrencyFilter('all'); setDateFromFilter(''); setDateToFilter(''); setAmountFromFilter(''); setAmountToFilter('');
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];
  const providerOptions = [
    { value: 'all', label: 'All Providers' },
    ...providers.map((provider: any) => ({ value: provider.documentId || provider.id, label: provider.name }))
  ];
  const bankOptions = [
    { value: 'all', label: 'All Banks' },
    ...banks.map((bank: any) => ({ value: bank.documentId || bank.id, label: bank.name }))
  ];
  const currencyOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Currencies' }];
    (currencies || []).filter((c: any) => c.isActive !== false).forEach((c: any) => {
      options.push({ value: c.code || c.documentId || c.id || '', label: c.code || '' });
    });
    return options;
  }, [currencies]);

  const filterConfigs = [
    { key: 'status', label: 'Status', type: 'select' as const, value: statusFilter, onChange: setStatusFilter, options: statusOptions },
    { key: 'provider', label: 'Provider', type: 'select' as const, value: providerFilter, onChange: setProviderFilter, options: providerOptions },
    { key: 'bank', label: 'Bank', type: 'select' as const, value: bankFilter, onChange: setBankFilter, options: bankOptions },
    { key: 'currency', label: 'Currency', type: 'select' as const, value: currencyFilter, onChange: setCurrencyFilter, options: currencyOptions },
    { key: 'dateFrom', label: 'Date From', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Amount From', type: 'number' as const, value: amountFromFilter, onChange: setAmountFromFilter, placeholder: 'Min amount' },
    { key: 'amountTo', label: 'Amount To', type: 'number' as const, value: amountToFilter, onChange: setAmountToFilter, placeholder: 'Max amount' }
  ];

  return { searchTerm, setSearchTerm, filteredPaymentOrders, clearFilters, filterConfigs };
}
